// src/components/layout/Sidebar.js
import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
	IconButton,
} from "@mui/material";
import {
	LayoutDashboard,
	Users,
	Database,
	School,
	Receipt,
	Calendar,
	Upload,
	MessagesSquare,
	UserCircle,
	LogOut,
	Building2,
	ClipboardList,
	Car,
	FileSpreadsheet,
	BookOpen,
	FileChartColumnIncreasing,
	X as CloseIcon,
	CheckIcon,
	Bell,
	BellIcon,
	Globe,
	ImagePlus,
	BarChart3,
	Newspaper,
	Briefcase,
	MessageSquareQuote,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";

// Menu configurations for different roles
const menuConfig = {
	ROLE_FUNMIYO_ADMIN: [
		{
			type: "item",
			text: "Dashboard",
			icon: LayoutDashboard,
			path: "/dashboard",
		},
		{
			type: "item",
			text: "Approvals",
			icon: CheckIcon,
			path: "/approvals",
		},
		{
			type: "item",
			text: "Notifications",
			icon: Bell,
			path: "/notifications",
		},
		{
			type: "item",
			text: "Game Results Edits",
			icon: Bell,
			path: "/game-results-edits",
		},
		{
			type: "item",
			text: "My Notifications",
			icon: BellIcon,
			path: "/my-notifications",
		},
		{
			type: "item",
			text: "Reports",
			icon: FileChartColumnIncreasing,
			path: "/reports/funmiyo-admin-report",
		},
		{
			type: "item",
			text: "Teacher Schedule",
			icon: FileChartColumnIncreasing,
			path: "/reports/teacher-schedule",
		},
		{
			type: "item",
			text: "Teacher Course Updates",
			icon: ClipboardList,
			path: "/teacher-course-updates",
		},
		{ type: "section", text: "Users" },
		{ type: "item", text: "Manage Users", icon: Users, path: "/manage-users" },
		{ type: "section", text: "CMS" },
		{ type: "item", text: "Master Data", icon: Database, path: "/master-data" },
		{
			type: "item",
			text: "Manage Games",
			icon: BookOpen,
			path: "/manage-games",
		},
		{ type: "section", text: "Schools" },
		{
			type: "item",
			text: "Manage Schools",
			icon: School,
			path: "/manage-schools",
		},
		{
			type: "item",
			text: "School Ownerships",
			icon: Building2,
			path: "/school-ownerships",
		},
		{ type: "item", text: "School Visits", icon: Car, path: "/school-visits" },
		{
			type: "item",
			text: "School Visit Tracker",
			icon: Calendar,
			path: "/school-visit-tracker",
		},
		{
			type: "item",
			text: "Service Window",
			icon: Calendar,
			path: "/service-window",
		},
		{ type: "section", text: "Report" },
		{
			type: "item",
			text: "Report Settings",
			icon: FileChartColumnIncreasing,
			path: "/report-settings",
		},
		{
			type: "item",
			text: "Report Generation",
			icon: FileChartColumnIncreasing,
			path: "/report-generation",
		},
		{
			type: "item",
			text: "Bulk Reports",
			icon: FileChartColumnIncreasing,
			path: "/bulk-reports",
		},
		{
			type: "item",
			text: "FME Performance Metrics",
			icon: FileChartColumnIncreasing,
			path: "/reports/fme-performance-metrics",
		},
		{ type: "section", text: "System" },
		{
			type: "item",
			text: "Academic Year",
			icon: Calendar,
			path: "/academic-year-settings",
		},
		{ type: "section", text: "Website Management" },
		{
			type: "item",
			text: "Web Settings",
			icon: Globe,
			path: "/web-settings",
		},
		{
			type: "item",
			text: "Web Stats",
			icon: BarChart3,
			path: "/web-stats",
		},
		{
			type: "item",
			text: "School Logos",
			icon: ImagePlus,
			path: "/school-logos",
		},
		{
			type: "item",
			text: "Newsletters",
			icon: Newspaper,
			path: "/newsletters",
		},
		{
			type: "item",
			text: "Open Positions",
			icon: Briefcase,
			path: "/open-positions",
		},
		{
			type: "item",
			text: "Testimonials",
			icon: MessageSquareQuote,
			path: "/testimonials",
		},
		{ type: "section", text: "Expenses" },
		{
			type: "item",
			text: "Submit Expense",
			icon: Receipt,
			path: "/submit-expense",
		},
		{
			type: "item",
			text: "My Expenses",
			icon: Receipt,
			path: "/my-expenses",
		},
		{
			type: "item",
			text: "Expense Reports",
			icon: FileSpreadsheet,
			path: "/expense-reports",
		},
	],
	ROLE_SCHOOL_TEACHER: [
		{
			type: "item",
			text: "Dashboard",
			icon: LayoutDashboard,
			path: "/dashboard",
			alwaysShow: true,
		},
		{
			type: "item",
			text: "My Notifications",
			icon: BellIcon,
			path: "/my-notifications",
		},
		{
			type: "item",
			text: "Timetable",
			icon: Calendar,
			path: "/schedule",
			requiresCompleteProfile: true,
		},
		{
			type: "item",
			text: "Upload Media",
			icon: Upload,
			path: "/game-results-upload",
			requiresCompleteProfile: true,
		},
	],
	ROLE_FUNMATH_ADMIN: [
		{
			type: "item",
			text: "Dashboard",
			icon: LayoutDashboard,
			path: "/dashboard",
		},
		{
			type: "item",
			text: "Game Results Edits",
			icon: Bell,
			path: "/game-results-edits",
		},
		{
			type: "item",
			text: "Reports",
			icon: FileChartColumnIncreasing,
			path: "/reports/funmath-owners-report",
		},
		{
			type: "item",
			text: "Teacher Schedule",
			icon: FileChartColumnIncreasing,
			path: "/reports/teacher-schedule",
		},
		{
			type: "item",
			text: "My Notifications",
			icon: BellIcon,
			path: "/my-notifications",
		},
		{
			type: "item",
			text: "All Notifications",
			icon: BellIcon,
			path: "/all-notifications",
		},
		{ type: "section", text: "CMS" },
		{ type: "item", text: "Manage Courses", icon: BookOpen, path: "/courses" },
		{ type: "section", text: "Schools" },
		{ type: "item", text: "School Visits", icon: Car, path: "/school-visits" },
		{
			type: "item",
			text: "Service Window",
			icon: Calendar,
			path: "/service-window",
		},
		{ type: "section", text: "Report" },
		{
			type: "item",
			text: "Report Generation",
			icon: FileChartColumnIncreasing,
			path: "/report-generation",
		},
		{
			type: "item",
			text: "Bulk Reports",
			icon: FileChartColumnIncreasing,
			path: "/bulk-reports",
		},
		{ type: "section", text: "Expenses" },
		{
			type: "item",
			text: "Submit Expense",
			icon: Receipt,
			path: "/submit-expense",
		},
		{
			type: "item",
			text: "My Expenses",
			icon: Receipt,
			path: "/my-expenses",
		},
	],
	ROLE_FUNMIYO_EMPLOYEE: [
		{
			type: "item",
			text: "Dashboard",
			icon: LayoutDashboard,
			path: "/dashboard",
		},
		{
			type: "item",
			text: "Game Results Edits",
			icon: Bell,
			path: "/game-results-edits",
		},
		{
			type: "item",
			text: "My Notifications",
			icon: BellIcon,
			path: "/my-notifications",
		},
		{
			type: "item",
			text: "All Notifications",
			icon: BellIcon,
			path: "/all-notifications",
		},
		{
			type: "item",
			text: "Reports",
			icon: FileChartColumnIncreasing,
			path: "/reports/funmath-owners-report",
		},
		{
			type: "item",
			text: "Teacher Schedule",
			icon: FileChartColumnIncreasing,
			path: "/reports/teacher-schedule",
		},
		{ type: "section", text: "Schools" },
		{ type: "item", text: "School Visits", icon: Car, path: "/school-visits" },
		{
			type: "item",
			text: "Service Window",
			icon: Calendar,
			path: "/service-window",
		},
		{ type: "item", text: "Manage Schools", icon: School, path: "/manage-schools" },
		{ type: "item", text: "Manage Users", icon: Users, path: "/manage-users" },
		{ type: "section", text: "Report" },
		{
			type: "item",
			text: "Report Generation",
			icon: FileChartColumnIncreasing,
			path: "/report-generation",
		},
		{
			type: "item",
			text: "Bulk Reports",
			icon: FileChartColumnIncreasing,
			path: "/bulk-reports",
		},
		{ type: "section", text: "Expenses" },
		{
			type: "item",
			text: "Submit Expense",
			icon: Receipt,
			path: "/submit-expense",
		},
		{
			type: "item",
			text: "My Expenses",
			icon: Receipt,
			path: "/my-expenses",
		},
	],
	ROLE_VIDEO_EDITOR: [
		{
			type: "item",
			text: "Game Results Edits",
			icon: Bell,
			path: "/game-results-edits",
		},
		{
			type: "item",
			text: "My Notifications",
			icon: BellIcon,
			path: "/my-notifications",
		},
	],
};

// Footer menu items (common for all roles except ROLE_SCHOOL_TEACHER)
const footerMenuItems = [
	{ text: "Feedback", icon: MessagesSquare, path: "/feedback" },
	{ text: "Account", icon: UserCircle, path: "/account" },
	{ text: "Logout", icon: LogOut, path: "/logout" },
];

const Sidebar = ({ mobile = false, onClose }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [userProfile, setUserProfile] = useState(null);
	const [isProfileComplete, setIsProfileComplete] = useState(false);

	useEffect(() => {
		const fetchUserProfile = async () => {
			if (!user) return;

			// If profile is already available from context, use it
			if (user.profile) {
				setUserProfile(user.profile);
				// Check if profile is complete
				const isComplete = !!(
					user.profile.full_name &&
					user.profile.email &&
					user.profile.phone_number &&
					user.profile.school_id &&
					user.profile.status === "Active"
				);
				setIsProfileComplete(isComplete);
				return;
			}

			// Otherwise, fetch it
			try {
				const { data, error } = await supabase
					.from("profiles")
					.select("*")
					.eq("id", user.id)
					.single();

				if (error) {
					// Handle profile not found gracefully
					if (error.code === "PGRST116") {
						console.warn("Profile not found for user in Sidebar:", user.id);
						setUserProfile(null);
						setIsProfileComplete(false);
						return;
					}
					throw error;
				}

				setUserProfile(data);

				// Check if profile is complete
				// You can define what makes a profile "complete" based on your requirements
				// This is a simple example - adjust according to your needs
				const isComplete = !!(
					data.full_name &&
					data.email &&
					data.phone_number &&
					data.school_id &&
					data.status === "Active"
				);

				setIsProfileComplete(isComplete);
			} catch (err) {
				console.error("Error fetching user profile:", err);
				setUserProfile(null);
				setIsProfileComplete(false);
			}
		};

		fetchUserProfile();
	}, [user, user?.profile]);

	// Get menu items based on user role and profile completion
	const getFilteredMenuItems = () => {
		// Use userProfile from state if available, otherwise fall back to user.profile from context
		const profile = userProfile || user?.profile;
		const role = profile?.role;

		if (!role) return [];

		const roleItems = menuConfig[role] || [];

		// If user is a teacher and profile is not complete, filter out items requiring complete profile
		if (role === "ROLE_SCHOOL_TEACHER" && !isProfileComplete) {
			return roleItems.filter(
				(item) => item.alwaysShow || !item.requiresCompleteProfile
			);
		}

		return roleItems;
	};

	const roleMenuItems = getFilteredMenuItems();

	const handleLogout = async () => {
		try {
			// Try to sign out - if it fails (e.g., 403), we'll still clear local state
			await supabase.auth.signOut();
		} catch (error) {
			console.error("Error during sign out:", error);
			// Even if signOut fails, clear local storage and navigate
			// This handles cases where the session is invalid (403 errors)
			try {
				// Clear Supabase auth tokens from localStorage
				const keys = Object.keys(localStorage);
				keys.forEach(key => {
					if (key.startsWith('sb-') && key.includes('auth-token')) {
						localStorage.removeItem(key);
					}
				});
			} catch (e) {
				console.error("Error clearing localStorage:", e);
			}
		} finally {
			// Always navigate to login, even if signOut failed
			navigate("/login", { replace: true });
		}
	};

	const handleNavigation = (path, text) => {
		if (text === "Logout") {
			handleLogout();
		} else {
			navigate(path);
			// Close sidebar if in mobile mode
			if (mobile && onClose) {
				onClose();
			}
		}
	};

	const MenuItem = ({ item }) => {
		const isActive = location.pathname === item.path;
		return (
			<ListItem
				onClick={() => handleNavigation(item.path, item.text)}
				sx={{
					height: "36px",
					borderRadius: "6px",
					mb: 0.5,
					backgroundColor: isActive ? "#f3f4f6" : "transparent",
					cursor: "pointer",
					"&:hover": {
						backgroundColor: "#f3f4f6",
					},
				}}>
				<ListItemIcon sx={{ minWidth: 32 }}>
					<item.icon size={18} color={isActive ? "#6366f1" : "#6b7280"} />
				</ListItemIcon>
				<ListItemText
					primary={item.text}
					sx={{
						m: 0,
						"& .MuiTypography-root": {
							fontSize: "13px",
							fontWeight: isActive ? 600 : 500,
							color: isActive ? "#6366f1" : "#6b7280",
						},
					}}
				/>
			</ListItem>
		);
	};

	return (
		<Box
			sx={{
				position: mobile ? "relative" : "fixed",
				left: 0,
				top: 0,
				bottom: 0,
				width: mobile ? "280px" : "200px",
				backgroundColor: "white",
				borderRight: "1px solid #e5e7eb",
				display: "flex",
				flexDirection: "column",
				height: mobile ? "100%" : "auto",
			}}>
			<Box
				sx={{
					height: "48px", // Matches header height
					borderBottom: "1px solid #e5e7eb",
					display: "flex",
					alignItems: "center", // Centers text vertically
					justifyContent: "space-between",
					px: 2, // Keeps consistent padding
				}}>
				<Box
					onClick={() => {
						navigate("/dashboard");
						if (mobile && onClose) onClose();
					}}
					sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
					<img
						src="/logo.png"
						alt="Funmiyo Portal"
						style={{
							height: "32px",
							width: "auto",
							objectFit: "contain",
						}}
					/>
				</Box>

				{mobile && (
					<IconButton onClick={onClose} size="small" sx={{ color: "#6b7280" }}>
						<CloseIcon size={18} />
					</IconButton>
				)}
			</Box>

			{/* Main Menu */}
			<Box sx={{ flex: 1, overflowY: "auto" }}>
				<List sx={{ px: 1, pt: 1 }}>
					{roleMenuItems.map((item, index) =>
						item.type === "section" ? (
							<Typography
								key={index}
								variant="caption"
								sx={{
									px: 2,
									py: 1,
									display: "block",
									color: "#6b7280",
									fontWeight: 600,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									fontSize: "11px",
								}}>
								{item.text}
							</Typography>
						) : (
							<MenuItem key={index} item={item} />
						)
					)}
				</List>
			</Box>

			<Divider />
			<List sx={{ px: 1, py: 1 }}>
				{footerMenuItems.map((item, index) => (
					<MenuItem key={index} item={item} />
				))}
			</List>
		</Box>
	);
};

export default Sidebar;
